import { jsPDF } from 'jspdf';
import moment from 'moment';
import { money, formatRowDateTime } from './orders.copy';
import { computeTotals } from './orders.selectors';

/**
 * Range report — the orders currently listed, with the filters that produced
 * them named in the header so the sheet can be read on its own a week later.
 *
 * Drawn by hand rather than with a table plugin: the app does not ship one,
 * and one column layout does not justify a dependency.
 */

const PAGE = { width: 595.28, height: 841.89 }; // A4 portrait, points
const MARGIN = 40;
const LINE = 16;

const COLUMNS = [
  { key: 'number', label: 'Order', x: MARGIN, align: 'left', width: 44 },
  { key: 'when', label: 'Date & time', x: MARGIN + 50, align: 'left', width: 106 },
  { key: 'customer', label: 'Customer', x: MARGIN + 162, align: 'left', width: 108 },
  { key: 'staff', label: 'Waiter', x: MARGIN + 276, align: 'left', width: 78 },
  { key: 'status', label: 'Status', x: MARGIN + 360, align: 'left', width: 50 },
  { key: 'discount', label: 'Discount', x: PAGE.width - MARGIN - 72, align: 'right', width: 60 },
  { key: 'total', label: 'Total', x: PAGE.width - MARGIN, align: 'right', width: 60 },
];

const statusLabel = (entry) => {
  if (entry.isInvoice) return 'Invoice';
  return entry.isPending ? 'Pending' : 'Paid';
};

const describe = (entry) => entry.customerName || '—';

function drawHeaderRow(doc, y) {
  doc.setFontSize(8).setFont('helvetica', 'bold').setTextColor(120);
  COLUMNS.forEach((col) => {
    doc.text(col.label.toUpperCase(), col.x, y, { align: col.align });
  });
  doc.setDrawColor(210).line(MARGIN, y + 5, PAGE.width - MARGIN, y + 5);
  return y + LINE;
}

/**
 * @param {Object}  input
 * @param {Array}   input.entries   view entries, already filtered and sorted
 * @param {Object}  input.business  the signed-in user's business
 * @param {Object}  input.range     {start, end} as YYYY-MM-DD
 * @param {string}  input.filters   human description of the active filters
 * @returns {string} the filename it saved
 */
export default function exportOrdersPdf({ entries, business, range, filters }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const currency = business?.currencyMark || '$';
  const totals = computeTotals(entries);

  let y = MARGIN;

  // ── Header ───────────────────────────────────────────────────────────────
  doc.setFontSize(15).setFont('helvetica', 'bold').setTextColor(20);
  doc.text(business?.businessName || 'Dalabtire', MARGIN, y);
  y += 18;

  doc.setFontSize(10).setFont('helvetica', 'normal').setTextColor(90);
  doc.text('Orders report', MARGIN, y);

  const rangeLabel =
    range.start === range.end
      ? moment(range.start).format('D MMM YYYY')
      : `${moment(range.start).format('D MMM YYYY')} – ${moment(range.end).format('D MMM YYYY')}`;
  doc.text(rangeLabel, PAGE.width - MARGIN, y, { align: 'right' });
  y += 14;

  doc.setFontSize(8).setTextColor(130);
  if (filters) {
    doc.text(filters, MARGIN, y);
  }
  doc.text(`Generated ${moment().format('D MMM YYYY HH:mm')}`, PAGE.width - MARGIN, y, {
    align: 'right',
  });
  y += 18;

  // ── Totals strip ─────────────────────────────────────────────────────────
  doc.setDrawColor(225).setFillColor(247, 247, 249);
  doc.rect(MARGIN, y, PAGE.width - MARGIN * 2, 44, 'F');

  const slots = [
    ['Total', totals.total],
    ['Cash', totals.cash],
    ['Invoice', totals.invoice],
    ['Pending', totals.pending],
    ['Discount', totals.discount],
  ];
  const slotWidth = (PAGE.width - MARGIN * 2) / slots.length;

  slots.forEach(([label, value], i) => {
    const x = MARGIN + slotWidth * i + 10;
    doc.setFontSize(7.5).setFont('helvetica', 'normal').setTextColor(130);
    doc.text(label.toUpperCase(), x, y + 16);
    doc.setFontSize(11).setFont('helvetica', 'bold').setTextColor(20);
    doc.text(money(value, currency), x, y + 32);
  });
  y += 60;

  doc.setFontSize(9).setFont('helvetica', 'normal').setTextColor(90);
  doc.text(`${entries.length} order${entries.length === 1 ? '' : 's'}`, MARGIN, y);
  y += 12;

  // ── Rows ─────────────────────────────────────────────────────────────────
  y = drawHeaderRow(doc, y);

  entries.forEach((entry) => {
    if (y > PAGE.height - MARGIN - LINE) {
      doc.addPage();
      y = MARGIN;
      y = drawHeaderRow(doc, y);
    }

    const cells = {
      number: `#${entry.orderNumber}`,
      when: formatRowDateTime(entry.time),
      customer: describe(entry),
      staff: entry.staffName || '—',
      status: statusLabel(entry),
      discount: entry.discount > 0 ? `−${money(entry.discount, currency)}` : '',
      total: money(entry.total, currency),
    };

    doc.setFontSize(9).setFont('helvetica', 'normal').setTextColor(35);
    COLUMNS.forEach((col) => {
      const value = String(cells[col.key] || '');
      if (!value) return;

      doc.setFont('helvetica', col.key === 'total' ? 'bold' : 'normal');
      doc.setTextColor(col.key === 'discount' ? 200 : 35, col.key === 'discount' ? 45 : 35, col.key === 'discount' ? 40 : 35);
      // Truncate rather than let a long name run into the next column.
      const text = doc.splitTextToSize(value, col.width)[0];
      doc.text(text, col.x, y, { align: col.align });
    });

    doc.setDrawColor(238).line(MARGIN, y + 4, PAGE.width - MARGIN, y + 4);
    y += LINE;
  });

  const filename = `orders-${range.start}${range.start === range.end ? '' : `_${range.end}`}.pdf`;
  doc.save(filename);
  return filename;
}
